import React from 'react'
import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// 常數定義
const SHAPE_TYPES = {
  0: '顆粒狀',
  1: '凹凸腸狀',
  2: '裂痕腸狀',
  3: '光滑條狀',
  4: '柔軟塊狀',
  5: '糊狀',
  6: '水狀',
}

const VOLUME_TYPES = {
  0: '少量',
  1: '正常',
  2: '多量',
}

const COLOR_TYPES = {
  0: '褐色',
  1: '黃色',
  2: '灰色',
  3: '黑色',
  4: '紅色',
  5: '墨綠色',
}

const DefecationRecord = ({
  data,
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  const getLocalDateString = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  // Process chart data
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) {
      return {
        labels: [],
        datasets: [{ data: [] }],
      }
    }

    // Generate date range
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(getLocalDateString(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }
    const allDates = Array.from(dateSet).sort()

    // 添加數據格式驗證
    const validRecords = data.filter(
      (record) =>
        record &&
        record.start_time &&
        record.end_time &&
        !isNaN(new Date(record.start_time).getTime()) &&
        !isNaN(new Date(record.end_time).getTime())
    )

    // 計算每條記錄的時間範圍
    const records = validRecords
      .map((record) => {
        try {
          const startDate = new Date(record.start_time)
          const endDate = new Date(record.end_time)

          // 使用本地時間的日期作為記錄日期
          const recordDate = getLocalDateString(endDate)

          // 先將日期統一到結束時間的日期
          const normalizedStartDate = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
            startDate.getHours(),
            startDate.getMinutes()
          )

          // 如果是跨天記錄，調整開始時間為0點
          // 使用本地日期字符串進行比較
          if (getLocalDateString(startDate) !== recordDate) {
            normalizedStartDate.setHours(0, 0, 0)
          }

          const startHours =
            normalizedStartDate.getHours() +
            normalizedStartDate.getMinutes() / 60
          const endHours = endDate.getHours() + endDate.getMinutes() / 60
          console.log('record', record)
          return {
            date: recordDate,
            timeRange: [startHours, endHours],
            // tooltip 要顯示的
            duration: record.duration,
            shape: record.shape,
            volume: record.volume,
            color: record.color,
            isAcrossDays: getLocalDateString(startDate) !== recordDate,
          }
        } catch (error) {
          console.error('Error processing record:', error, record)
          return null
        }
      })
      .filter(Boolean)

    // Format date labels
    const formatDateTime = (dateStr) => {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    return {
      labels: allDates.map(formatDateTime),
      datasets: [
        {
          label: '排便時段',
          data: allDates
            .map((date) => {
              const dayRecords = records.filter((r) => r.date === date)
              if (!dayRecords.length) return null
              return dayRecords.map((record) => ({
                x: formatDateTime(date),
                y: record.timeRange,
                duration: record.duration,
                shape: record.shape,
                volume: record.volume,
                color: record.color,
              }))
            })
            .flat()
            .filter(Boolean),
          backgroundColor: 'rgba(75, 192, 192, 0.7)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const data = context.raw
            // const timeRange = data.y
            // const startTime = `${Math.floor(timeRange[0])}:${String(
            //   Math.round((timeRange[0] % 1) * 60)
            // ).padStart(2, '0')}`
            // const endTime = `${Math.floor(timeRange[1])}:${String(
            //   Math.round((timeRange[1] % 1) * 60)
            // ).padStart(2, '0')}`
            const lines = [
              // `時間：${startTime} ~ ${endTime}`,
              `時長：${data.duration} 分鐘`,
              `形狀：${SHAPE_TYPES[data.shape]}`,
              `份量：${VOLUME_TYPES[data.volume]}`,
              `顏色：${COLOR_TYPES[data.color]}`,
            ]

            return lines
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 24,
        ticks: {
          stepSize: 3,
          callback: function (value) {
            return `${String(value).padStart(2, '0')}:00`
          },
        },
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        title: {
          display: true,
          text: '時間',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  }

  // Group data by date for table display
  const groupDataByDate = (records) => {
    if (!startDate || !endDate || !Array.isArray(records)) return []

    // Generate date range
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(getLocalDateString(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // Convert to array and sort (descending, newest first)
    let allDates = Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a))

    // 添加數據驗證
    const validRecords = records.filter(
      (record) =>
        record &&
        record.start_time &&
        record.end_time &&
        !isNaN(new Date(record.start_time).getTime()) &&
        !isNaN(new Date(record.end_time).getTime())
    )

    // Create date to records mapping using end_time as the record date
    const recordsByDate = validRecords.reduce((acc, record) => {
      try {
        // 使用結束時間的日期作為記錄日期
        const endDate = new Date(record.end_time)
        const date = getLocalDateString(endDate)
        if (!acc[date]) acc[date] = []
        acc[date].push(record)
        return acc
      } catch (error) {
        console.error('Error processing record for table:', error, record)
        return acc
      }
    }, {})

    // Create daily data structure
    return allDates.map((date) => {
      const dayRecords = recordsByDate[date] || []
      return {
        date,
        records: dayRecords.sort((a, b) => {
          try {
            return new Date(b.end_time) - new Date(a.end_time)
          } catch {
            return 0
          }
        }),
      }
    })
  }
  // Format time
  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-'
    const date = new Date(dateTimeStr)
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`
  }

  const formatTimeRange = (startTime, endTime) => {
    const start = new Date(startTime)
    const end = new Date(endTime)

    // 格式化時間
    const formatTime = (date) => {
      return `${String(date.getHours()).padStart(2, '0')}:${String(
        date.getMinutes()
      ).padStart(2, '0')}`
    }

    // 檢查是否跨天
    const isCrossDay = getLocalDateString(start) !== getLocalDateString(end)

    if (isCrossDay) {
      return `(前日) ${formatTime(start)} ~ ${formatTime(end)}`
    } else {
      return `${formatTime(start)} ~ ${formatTime(end)}`
    }
  }

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  const groupedData = groupDataByDate(data || [])

  return (
    <Row className="defecation-record g-3">
      {/* Chart Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <Card.Title className="d-flex justify-content-end align-items-center mb-4">
              <div className="ms-3 d-flex">
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(75, 192, 192, 0.7)' }}
                  ></i>
                  排便時段
                </span>
              </div>
            </Card.Title>
            <div className="responsive-chart">
              <Bar options={chartOptions} data={processChartData()} />
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Table Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {groupedData.map((dayGroup) => (
                <Table
                  key={dayGroup.date}
                  hover
                  className="mb-0 border rounded-3 overflow-hidden"
                >
                  <thead>
                    <tr className="table-light">
                      <th colSpan="5">{formatDate(dayGroup.date)}</th>
                      <th className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="px-3"
                          onClick={() => {
                            setShowAddForm(true)
                            onAddRecord?.(dayGroup.date)
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          新增
                        </Button>
                      </th>
                    </tr>
                    {/* {dayGroup.records.length > 0 && (
                      <tr className="table-light">
                        <th>時間</th>
                        <th>時長</th>
                        <th>形狀</th>
                        <th>量</th>
                        <th>顏色</th>
                        <th>備註</th>
                      </tr>
                    )} */}
                  </thead>
                  <tbody>
                    {dayGroup.records.length > 0 ? (
                      dayGroup.records.map((record) => (
                        <tr key={record.action_id}>
                          <td>
                            {formatTimeRange(
                              record.start_time,
                              record.end_time
                            )}
                          </td>
                          <td>{record.duration} 分鐘</td>
                          <td>{SHAPE_TYPES[record.shape]}</td>
                          <td>{VOLUME_TYPES[record.volume]}</td>
                          <td>{COLOR_TYPES[record.color]}</td>
                          <td>{record.comment || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          尚無排便紀錄
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default DefecationRecord
